import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { fetchMyPosts, deleteMyPost, studioAuth, fetchMyActiveLive } from "../lib/studioApi";
import PostCard from "../components/studio/PostCard";
import UserComposeBox from "../components/studio/UserComposeBox";
import LiveBanner from "../components/studio/LiveBanner";
import { Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  "Inquiry Received": "bg-white/10 text-white/70",
  "Call Scheduled": "bg-blue-500/20 text-blue-300",
  Songwriting: "bg-purple-500/20 text-purple-300",
  Production: "bg-amber-500/20 text-amber-300",
  Mixing: "bg-orange-500/20 text-orange-300",
  Mastering: "bg-pink-500/20 text-pink-300",
  "Ready For Release": "bg-emerald-500/20 text-emerald-300",
  Completed: "bg-paul-yellow/20 text-paul-yellow",
};

export default function StudioHome() {
  const { me } = useOutletContext();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveSession, setLiveSession] = useState(null);

  const load = () => {
    fetchMyPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const refreshLive = () => {
      fetchMyActiveLive()
        .then(setLiveSession)
        .catch(() => {});
    };
    refreshLive();
    const id = setInterval(refreshLive, 15000);
    return () => clearInterval(id);
  }, []);

  const myId = me?.id || studioAuth.getUser()?.id;
  const onDeleteOwn = async (post) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await deleteMyPost(post.id);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      {/* Sticky live banner (Paul has gone live) */}
      <LiveBanner session={liveSession} />

      {/* Welcome / status header */}
      <section className="bg-gradient-to-br from-[#10162a] to-[#161a2c] border border-white/5 rounded-2xl p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-paul-yellow font-semibold mb-2">
              My Studio
            </p>
            <h1 className="text-3xl md:text-4xl font-extrabold">
              {me?.artistName || me?.name || "Welcome"}
            </h1>
            {me?.artistName && me?.name && me.artistName !== me.name && (
              <p className="text-white/50 mt-1">{me.name}</p>
            )}
          </div>
          {me?.status && (
            <div className="text-left md:text-right">
              <div className="text-xs uppercase tracking-wider text-white/40 mb-1">Current stage</div>
              <span
                className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold ${
                  STATUS_COLORS[me.status] || "bg-white/10 text-white"
                }`}
              >
                {me.status}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Artist-side composer */}
      <UserComposeBox me={me} onPosted={load} />

      <section>
        <h2 className="text-xl font-bold mb-4 text-white/90">Latest Updates</h2>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-white/50">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-[#10162a] border border-white/5 rounded-2xl p-10 text-center">
            <Inbox className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No updates yet. Share a demo or note above to start the conversation.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {posts.map((p) => (
              <PostCard
                key={p.id}
                post={p}
                onDelete={
                  p.authorRole === "user" && p.authorId === myId ? onDeleteOwn : undefined
                }
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
